import { ApiError, ERROR_CODES, type ErrorCode } from './errors.js';
import { readSseStream } from './sse.js';
import type {
  AuthSession,
  BodyShot,
  CaptionRequest,
  CaptionResponse,
  ChangePasswordRequest,
  ChatMessage,
  CreateBodyShotRequest,
  CreateLookbookRequest,
  CreateRoomRequest,
  Drop,
  FeedQuery,
  ForgotPasswordRequest,
  Garment,
  GarmentQuery,
  GoogleAuthRequest,
  HypeRequest,
  Job,
  LoginRequest,
  Look,
  Lookbook,
  Notification,
  Page,
  PlatformStats,
  PublicUser,
  RegisterRequest,
  ResetPasswordRequest,
  Room,
  StylistChatRequest,
  TryOnRequest,
  UpdateLookRequest,
  UpdateLookbookRequest,
  UpdateMeRequest,
  User,
} from './schemas.js';

/**
 * Token storage. Each platform supplies its own:
 *  - web/electron: localStorage
 *  - mobile: expo-secure-store
 */
export interface TokenStore {
  get(): Promise<StoredTokens | null> | StoredTokens | null;
  set(tokens: StoredTokens | null): Promise<void> | void;
}

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export class MemoryTokenStore implements TokenStore {
  private tokens: StoredTokens | null = null;
  get() {
    return this.tokens;
  }
  set(tokens: StoredTokens | null) {
    this.tokens = tokens;
  }
}

export interface RackSavantClientOptions {
  baseUrl: string;
  tokenStore?: TokenStore;
  /** Called whenever the session is cleared, so the UI can bounce to sign-in. */
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, unknown>;
  auth?: boolean;
  signal?: AbortSignal;
  /** Skip the refresh-and-retry dance (used by refresh itself). */
  noRetry?: boolean;
}

export class RackSavantClient {
  readonly baseUrl: string;
  private readonly tokenStore: TokenStore;
  private readonly onUnauthorized?: () => void;
  private readonly fetchImpl: typeof fetch;
  private refreshInFlight: Promise<StoredTokens | null> | null = null;

  constructor(options: RackSavantClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.tokenStore = options.tokenStore ?? new MemoryTokenStore();
    this.onUnauthorized = options.onUnauthorized;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /* ------------------------------ plumbing ------------------------------ */

  private buildUrl(path: string, query?: Record<string, unknown>): string {
    const url = new URL(this.baseUrl + path);
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null || value === '') continue;
        if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, String(v)));
        else url.searchParams.set(key, String(value));
      }
    }
    return url.toString();
  }

  private async accessToken(): Promise<string | null> {
    const tokens = await this.tokenStore.get();
    if (!tokens) return null;
    // Refresh a minute early so we never race the expiry.
    if (tokens.expiresAt - 60_000 < Date.now()) {
      const refreshed = await this.refresh();
      return refreshed?.accessToken ?? null;
    }
    return tokens.accessToken;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, query, auth = true, signal, noRetry } = options;

    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (auth) {
      const token = await this.accessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(this.buildUrl(path, query), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      });
    } catch (cause) {
      if ((cause as Error)?.name === 'AbortError') throw cause;
      throw new ApiError(ERROR_CODES.NETWORK, undefined, 0);
    }

    if (response.status === 401 && auth && !noRetry) {
      const refreshed = await this.refresh();
      if (refreshed) return this.request<T>(path, { ...options, noRetry: true });
      await this.clearSession();
      throw ApiError.unauthorized();
    }

    if (!response.ok) throw await toApiError(response);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  /* -------------------------------- auth -------------------------------- */

  private async persist(session: AuthSession): Promise<AuthSession> {
    await this.tokenStore.set({
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      expiresAt: session.expiresAt,
    });
    return session;
  }

  async register(input: RegisterRequest): Promise<AuthSession> {
    const session = await this.request<AuthSession>('/v1/auth/register', {
      method: 'POST',
      body: input,
      auth: false,
    });
    return this.persist(session);
  }

  async login(input: LoginRequest): Promise<AuthSession> {
    const session = await this.request<AuthSession>('/v1/auth/login', {
      method: 'POST',
      body: input,
      auth: false,
    });
    return this.persist(session);
  }

  async loginWithGoogle(input: GoogleAuthRequest): Promise<AuthSession> {
    const session = await this.request<AuthSession>('/v1/auth/google', {
      method: 'POST',
      body: input,
      auth: false,
    });
    return this.persist(session);
  }

  async refresh(): Promise<StoredTokens | null> {
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = (async () => {
      try {
        const current = await this.tokenStore.get();
        if (!current?.refreshToken) return null;
        const session = await this.request<AuthSession>('/v1/auth/refresh', {
          method: 'POST',
          body: { refreshToken: current.refreshToken },
          auth: false,
          noRetry: true,
        });
        await this.persist(session);
        return {
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          expiresAt: session.expiresAt,
        };
      } catch {
        await this.clearSession();
        return null;
      } finally {
        this.refreshInFlight = null;
      }
    })();
    return this.refreshInFlight;
  }

  async logout(): Promise<void> {
    try {
      const current = await this.tokenStore.get();
      if (current?.refreshToken) {
        await this.request<void>('/v1/auth/logout', {
          method: 'POST',
          body: { refreshToken: current.refreshToken },
          noRetry: true,
        });
      }
    } catch {
      // Signing out locally always succeeds, even if the network doesn't.
    }
    await this.tokenStore.set(null);
  }

  /**
   * Always resolves `{ ok: true }`, whether or not that email has an account.
   * Do not build UI that infers anything from the result — there is nothing
   * in it to infer from, by design.
   */
  forgotPassword(input: ForgotPasswordRequest): Promise<{ ok: true }> {
    return this.request<{ ok: true }>('/v1/auth/forgot-password', {
      method: 'POST',
      body: input,
      auth: false,
    });
  }

  /** Signs the user straight in — a reset that ends at a login form is a chore. */
  async resetPassword(input: ResetPasswordRequest): Promise<AuthSession> {
    const session = await this.request<AuthSession>('/v1/auth/reset-password', {
      method: 'POST',
      body: input,
      auth: false,
    });
    return this.persist(session);
  }

  /**
   * Every other session is signed out; this one survives because we hand the
   * server our own refresh token to spare.
   */
  async changePassword(input: ChangePasswordRequest): Promise<{ ok: true }> {
    const current = await this.tokenStore.get();
    return this.request<{ ok: true }>('/v1/auth/change-password', {
      method: 'POST',
      body: { ...input, keepRefreshToken: input.keepRefreshToken ?? current?.refreshToken },
    });
  }

  private async clearSession(): Promise<void> {
    await this.tokenStore.set(null);
    this.onUnauthorized?.();
  }

  async hasSession(): Promise<boolean> {
    return Boolean(await this.tokenStore.get());
  }

  /* --------------------------------- me --------------------------------- */

  me(): Promise<User> {
    return this.request<User>('/v1/me');
  }

  updateMe(input: UpdateMeRequest): Promise<User> {
    return this.request<User>('/v1/me', { method: 'PATCH', body: input });
  }

  listBodyShots(): Promise<BodyShot[]> {
    return this.request<BodyShot[]>('/v1/me/bodyshots');
  }

  createBodyShot(input: CreateBodyShotRequest): Promise<BodyShot> {
    return this.request<BodyShot>('/v1/me/bodyshots', { method: 'POST', body: input });
  }

  deleteBodyShot(id: string): Promise<void> {
    return this.request<void>(`/v1/me/bodyshots/${id}`, { method: 'DELETE' });
  }

  setPrimaryBodyShot(id: string): Promise<BodyShot> {
    return this.request<BodyShot>(`/v1/me/bodyshots/${id}/primary`, { method: 'POST' });
  }

  notifications(): Promise<Notification[]> {
    return this.request<Notification[]>('/v1/me/notifications');
  }

  markNotificationsRead(): Promise<void> {
    return this.request<void>('/v1/me/notifications/read', { method: 'POST' });
  }

  /* -------------------------------- stats ------------------------------- */

  /** Live public counts. Unauthenticated; safe to call from the marketing site. */
  stats(): Promise<PlatformStats> {
    return this.request<PlatformStats>('/v1/stats', { auth: false });
  }

  /* ------------------------------- gallery ------------------------------ */

  garments(query: Partial<GarmentQuery> = {}): Promise<Page<Garment>> {
    return this.request<Page<Garment>>('/v1/garments', { query, auth: false });
  }

  garment(id: string): Promise<Garment> {
    return this.request<Garment>(`/v1/garments/${id}`, { auth: false });
  }

  drops(): Promise<Drop[]> {
    return this.request<Drop[]>('/v1/drops', { auth: false });
  }

  drop(slug: string): Promise<Drop & { garments: Garment[] }> {
    return this.request(`/v1/drops/${slug}`, { auth: false });
  }

  /* ------------------------------- try-on ------------------------------- */

  createTryOn(input: TryOnRequest): Promise<Job> {
    return this.request<Job>('/v1/tryon', { method: 'POST', body: input });
  }

  job(id: string): Promise<Job> {
    return this.request<Job>(`/v1/jobs/${id}`);
  }

  /** WebSocket URL for watching a job develop. */
  async jobStreamUrl(id: string): Promise<string> {
    const token = await this.accessToken();
    const url = new URL(`${this.baseUrl}/v1/jobs/${id}/stream`);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    if (token) url.searchParams.set('token', token);
    return url.toString();
  }

  /** Poll a job to completion. Used where sockets aren't available. */
  async waitForJob(
    id: string,
    onUpdate?: (job: Job) => void,
    options: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {},
  ): Promise<Job> {
    const interval = options.intervalMs ?? 1500;
    const deadline = Date.now() + (options.timeoutMs ?? 180_000);
    for (;;) {
      const job = await this.job(id);
      onUpdate?.(job);
      if (job.status === 'succeeded' || job.status === 'failed' || job.status === 'canceled') {
        return job;
      }
      if (Date.now() > deadline) throw ApiError.upstream('That took too long. Try again.');
      await new Promise((resolve) => setTimeout(resolve, interval));
      if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    }
  }

  /* -------------------------------- looks ------------------------------- */

  myLooks(query: { cursor?: string; limit?: number } = {}): Promise<Page<Look>> {
    return this.request<Page<Look>>('/v1/looks', { query });
  }

  look(id: string): Promise<Look> {
    return this.request<Look>(`/v1/looks/${id}`);
  }

  updateLook(id: string, input: UpdateLookRequest): Promise<Look> {
    return this.request<Look>(`/v1/looks/${id}`, { method: 'PATCH', body: input });
  }

  deleteLook(id: string): Promise<void> {
    return this.request<void>(`/v1/looks/${id}`, { method: 'DELETE' });
  }

  hype(lookId: string, input: HypeRequest): Promise<Look> {
    return this.request<Look>(`/v1/looks/${lookId}/hype`, { method: 'POST', body: input });
  }

  caption(input: CaptionRequest): Promise<CaptionResponse> {
    return this.request<CaptionResponse>('/v1/looks/caption', { method: 'POST', body: input });
  }

  /* -------------------------------- feed -------------------------------- */

  feed(query: Partial<FeedQuery> = {}): Promise<Page<Look>> {
    return this.request<Page<Look>>('/v1/feed', { query });
  }

  /**
   * `following` reflects the requesting viewer and is `false` for anonymous
   * requests, so send auth when you have it — otherwise the follow button
   * paints the wrong state until the user clicks it.
   */
  profile(username: string): Promise<{
    user: PublicUser;
    looks: Look[];
    lookbooks: Lookbook[];
    following: boolean;
  }> {
    return this.request(`/v1/users/${username}`);
  }

  follow(userId: string, on = true): Promise<{ following: boolean }> {
    return this.request(`/v1/users/${userId}/follow`, { method: 'POST', body: { on } });
  }

  /* ------------------------------ lookbooks ----------------------------- */

  lookbooks(): Promise<Lookbook[]> {
    return this.request<Lookbook[]>('/v1/lookbooks');
  }

  createLookbook(input: CreateLookbookRequest): Promise<Lookbook> {
    return this.request<Lookbook>('/v1/lookbooks', { method: 'POST', body: input });
  }

  updateLookbook(id: string, input: UpdateLookbookRequest): Promise<Lookbook> {
    return this.request<Lookbook>(`/v1/lookbooks/${id}`, { method: 'PATCH', body: input });
  }

  deleteLookbook(id: string): Promise<void> {
    return this.request<void>(`/v1/lookbooks/${id}`, { method: 'DELETE' });
  }

  /* ------------------------------- rooms -------------------------------- */

  createRoom(input: CreateRoomRequest): Promise<Room> {
    return this.request<Room>('/v1/rooms', { method: 'POST', body: input });
  }

  room(code: string): Promise<Room & { looks: Look[] }> {
    return this.request(`/v1/rooms/${code}`);
  }

  joinRoom(code: string): Promise<Room> {
    return this.request<Room>(`/v1/rooms/${code}/join`, { method: 'POST' });
  }

  async roomSocketUrl(code: string): Promise<string> {
    const token = await this.accessToken();
    const url = new URL(`${this.baseUrl}/v1/rooms/${code}/live`);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    if (token) url.searchParams.set('token', token);
    return url.toString();
  }

  /* ------------------------------- stylist ------------------------------ */

  /** Streaming stylist chat. Yields text deltas as they arrive. */
  async *stylistStream(
    input: StylistChatRequest,
    signal?: AbortSignal,
  ): AsyncGenerator<{ delta?: string; done?: ChatMessage }> {
    const token = await this.accessToken();
    const response = await this.fetchImpl(this.buildUrl('/v1/stylist/chat'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(input),
      signal,
    });

    if (!response.ok) throw await toApiError(response);
    if (!response.body) throw ApiError.upstream();

    for await (const payload of readSseStream(response.body, signal)) {
      if (payload === '[DONE]') return;
      let frame: { delta?: string; done?: ChatMessage; error?: { code: string; message: string } };
      try {
        frame = JSON.parse(payload);
      } catch {
        continue; // keep-alive or malformed frame
      }
      if (frame.error) {
        throw new ApiError(frame.error.code as ErrorCode, frame.error.message, 502);
      }
      yield frame;
    }
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let code: ErrorCode = ERROR_CODES.INTERNAL;
  let message: string | undefined;
  let fields: Record<string, string> | undefined;
  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string; fields?: Record<string, string> };
    };
    if (body?.error?.code) code = body.error.code as ErrorCode;
    message = body?.error?.message;
    fields = body?.error?.fields;
  } catch {
    // Non-JSON error body — fall back to status-derived defaults.
  }
  return new ApiError(code, message, response.status, fields);
}
